/**
 * Renders the `chart` core view (ported from the MEIS-13490 prototype's
 * `AgentChartView.tsx`): a bar, line or pie chart built from a static spec a
 * module composed — no live data source. Validates the spec first: a
 * malformed one renders a small warning instead of crashing the message.
 *
 * The prototype did not render `title`/`description` itself (another
 * component around it did); here nothing else does, so this component shows
 * them above the chart.
 */
import { Alert, Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { useFormatting } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { AgentViewComponentProps } from "./registry.js";
import { validateChartView, type ChartViewSpec } from "./agentChartSpec.js";

const CHART_HEIGHT = 240;

export function AgentChartView({ view }: AgentViewComponentProps) {
  const { t } = useTranslate(["Assistant"]);
  const theme = useTheme();
  const { getFormattedFloat } = useFormatting();

  const problems = validateChartView(view);
  if (problems.length > 0)
    return (
      <Alert severity="warning" sx={{ py: 0 }}>
        {t("Chart could not be shown")}
      </Alert>
    );

  // Safe: `validateChartView` above checked every field this type relies on
  // (title, chartType, description, unit, categories, series).
  const spec = view as ChartViewSpec;

  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];
  const showLegend = spec.series.length > 1 || spec.chartType === "pie";
  const valueFormatter = (value: number | null) => {
    if (value === null) return "";
    const formatted = getFormattedFloat(value);
    return spec.unit ? `${formatted} ${spec.unit}` : formatted;
  };

  const chart =
    spec.chartType === "pie" ? (
      <PieChart
        height={CHART_HEIGHT}
        colors={colors}
        hideLegend={!showLegend}
        series={[
          {
            valueFormatter: (item: { value: number }) =>
              valueFormatter(item.value),
            data: spec.categories.map((category, index) => ({
              id: category,
              label: category,
              value: spec.series[0].data[index],
            })),
          },
        ]}
      />
    ) : (
      (() => {
        const xAxis = [{ data: spec.categories, scaleType: "band" as const }];
        const yAxis = [{ valueFormatter }];
        const series = spec.series.map(s => ({
          label: s.label,
          data: s.data,
          valueFormatter,
          ...(spec.chartType === "bar" && spec.stacked
            ? { stack: "total" }
            : {}),
        }));
        return spec.chartType === "bar" ? (
          <BarChart
            height={CHART_HEIGHT}
            colors={colors}
            hideLegend={!showLegend}
            xAxis={xAxis}
            yAxis={yAxis}
            series={series}
          />
        ) : (
          <LineChart
            height={CHART_HEIGHT}
            colors={colors}
            hideLegend={!showLegend}
            xAxis={xAxis}
            yAxis={yAxis}
            series={series}
          />
        );
      })()
    );

  return (
    <Stack spacing={0.25} sx={{ my: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {spec.title}
      </Typography>
      {spec.description && (
        <Typography variant="caption" color="text.secondary">
          {spec.description}
        </Typography>
      )}
      <Box>{chart}</Box>
    </Stack>
  );
}
